// Configuración de autovalidación por kms (servicios PT TP).
//
// Pantalla de settings globales con dos umbrales de autovalidación:
//   - Tope de kms por defecto para servicios cortos (default 50 km).
//   - Tolerancia de diferencia de kms en % entre lo registrado y lo saneado
//     (default 10%).
// La UI trabaja la tolerancia como PORCENTAJE entero (10) y convierte a
// fracción (0.10) al enviar al backend (ver autovalidationConfig.js).
//
// Endpoint PROPUESTO pendiente de backend (ver urls.jsx / api_rombo_helper.jsx):
// GET/PUT /api/v1/taxi/platform/service-validation/autovalidation-config.
// Si el GET responde 404, la pantalla degrada con elegancia: muestra los
// valores por defecto y un aviso de que la configuración aún no está disponible
// en el servidor. Cualquier otro error se trata como fallo de carga.
import React, { useCallback, useEffect, useState } from "react"
import { useSelector } from "react-redux"
import { createSelector } from "reselect"
import { useAuth0 } from "@auth0/auth0-react"
import { useFormik } from "formik"
import {
  Container, Row, Col, Card, CardBody, CardTitle, Spinner, Alert, Button,
  Form, FormGroup, Label, Input, FormFeedback, FormText, Table,
} from "reactstrap"
import Select from "react-select"
import { ToastContainer, toast } from "react-toastify"
import "react-toastify/dist/ReactToastify.css"
import { withTranslation } from "react-i18next"

import { Permissions } from "../../constants/enums"
import {
  getAutovalidationConfig, saveAutovalidationConfig, getOrgsAdmin,
  getAutovalidationConfigAudit,
} from "../../helpers/api_rombo_helper"
import {
  DEFAULT_AUTOVAL_CONFIG, toForm, toApi, buildAutovalSchema,
} from "./autovalidationConfig"

const ServiceValidationAutovalConfig = ({ t }) => {
  const { getAccessTokenSilently } = useAuth0()

  const [infoUser, setInfoUser] = useState({ permissions: [] })
  const [isLoading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  // notAvailable: el endpoint propuesto aún no existe en backend (404). Se
  // muestran los defaults y un aviso, sin tratarlo como error.
  const [notAvailable, setNotAvailable] = useState(false)
  const [initialValues, setInitialValues] = useState(() => toForm(DEFAULT_AUTOVAL_CONFIG))
  // Opciones del selector de empresas ([{label:name, value:partner_id}]) y la
  // bitácora de cambios de configuración (quién/cuándo/diff).
  const [companies, setCompanies] = useState([])
  const [auditEntries, setAuditEntries] = useState([])
  const [auditError, setAuditError] = useState(false)

  const LoginProperties = createSelector(
    (state) => state.Login,
    (Login) => ({ data: Login.data, loading: Login.loading }),
  )
  const { data: loginData, loading } = useSelector(LoginProperties)

  useEffect(() => {
    if (loginData?.data) setInfoUser(loginData.data)
  }, [loginData, loading])

  const canWrite = infoUser.permissions?.includes(Permissions.SERVICE_VALIDATION_CONFIG)

  const authHeader = useCallback(async () => {
    const accessToken = await getAccessTokenSilently()
    return { Authorization: `Bearer ${accessToken}` }
  }, [getAccessTokenSilently])

  // Carga la bitácora de cambios de configuración (best-effort: un fallo solo
  // marca el aviso y deja la lista vacía, no bloquea la pantalla).
  const loadAudit = useCallback(async () => {
    setAuditError(false)
    try {
      const token = await authHeader()
      const res = await getAutovalidationConfigAudit(token)
      setAuditEntries(Array.isArray(res?.entries) ? res.entries : [])
    } catch (_e) {
      setAuditEntries([])
      setAuditError(true)
    }
  }, [authHeader])

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    setNotAvailable(false)
    try {
      const token = await authHeader()
      const res = await getAutovalidationConfig(token)
      setInitialValues(toForm(res))
    } catch (e) {
      // 404: el endpoint propuesto aún no está implementado -> defaults + aviso.
      if (e?.response?.status === 404) {
        setNotAvailable(true)
        setInitialValues(toForm(DEFAULT_AUTOVAL_CONFIG))
      } else {
        setError(t("LoadError") || "No se pudo cargar la configuración")
      }
    } finally {
      setLoading(false)
    }
  }, [authHeader, t])

  useEffect(() => { load() }, [load])
  useEffect(() => { loadAudit() }, [loadAudit])

  // Pobla el selector de empresas con el mismo origen que lista/kanban.
  useEffect(() => {
    let active = true
    ;(async () => {
      try {
        const token = await authHeader()
        const resp = await getOrgsAdmin(token)
        const opts = (resp || []).map((o) => ({ label: o.name, value: String(o.partner_id) }))
        if (active) setCompanies(opts)
      } catch (_e) {
        if (active) setCompanies([])
      }
    })()
    return () => { active = false }
  }, [authHeader])

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema: buildAutovalSchema(t),
    onSubmit: async (values) => {
      setSaving(true)
      try {
        const token = await authHeader()
        await saveAutovalidationConfig(token, toApi(values))
        setNotAvailable(false)
        toast.success(t("AutovalSaved") || "Configuración guardada")
        // Refresca la bitácora para reflejar el cambio recién guardado.
        loadAudit()
      } catch (_e) {
        toast.error(t("AutovalSaveError") || "No se pudo guardar la configuración")
      } finally {
        setSaving(false)
      }
    },
  })

  const capInvalid = !!(formik.touched.kms_default_cap && formik.errors.kms_default_cap)
  const pctInvalid = !!(formik.touched.kms_tolerance_pct && formik.errors.kms_tolerance_pct)
  const devInvalid = !!(
    formik.touched.address_deviation_tolerance_m && formik.errors.address_deviation_tolerance_m
  )

  return (
    <div className="page-content">
      <Container fluid>
        <Row className="mb-3">
          <Col xs={12}>
            <h4 className="mb-0">
              <i className="bx bx-cog me-2" />
              {t("ServiceValidation")} · {t("AutovalConfig") || "Configuración"}
            </h4>
          </Col>
        </Row>

        {error && <Alert color="danger">{error}</Alert>}

        {notAvailable && (
          <Alert color="warning">
            <i className="bx bx-info-circle me-1" />
            {t("AutovalNotAvailable") ||
              "La configuración de autovalidación aún no está disponible en el servidor. Mostrando valores por defecto."}
          </Alert>
        )}

        {isLoading ? (
          <div className="text-center py-5"><Spinner color="primary" /></div>
        ) : (
          <Row>
            <Col lg={6}>
              <Card>
                <CardBody>
                  <CardTitle tag="h5" className="mb-3">
                    {t("AutovalConfigTitle") || "Umbrales de autovalidación por kms"}
                  </CardTitle>

                  <Form onSubmit={formik.handleSubmit} noValidate>
                    {/* Tope de kms por defecto (servicios cortos). Entero > 0. */}
                    <FormGroup>
                      <Label htmlFor="kms_default_cap">
                        {t("AutovalKmsDefaultCap") || "Tope de kms por defecto (servicios cortos)"}
                      </Label>
                      <Input
                        id="kms_default_cap"
                        name="kms_default_cap"
                        type="number"
                        min={1}
                        step={1}
                        value={formik.values.kms_default_cap}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        invalid={capInvalid}
                        aria-invalid={capInvalid}
                        aria-describedby="kms_default_cap_help"
                      />
                      <FormText id="kms_default_cap_help">
                        {t("AutovalKmsDefaultCapHelp") ||
                          "Unidad: KM. Los servicios con kms por debajo de este tope se consideran cortos."}
                      </FormText>
                      {capInvalid && (
                        <FormFeedback role="alert" aria-live="assertive">
                          {formik.errors.kms_default_cap}
                        </FormFeedback>
                      )}
                    </FormGroup>

                    {/* Tolerancia de diferencia de kms en PORCENTAJE entero (0-100). */}
                    <FormGroup>
                      <Label htmlFor="kms_tolerance_pct">
                        {t("AutovalKmsTolerancePct") || "Tolerancia de diferencia de kms (%)"}
                      </Label>
                      <Input
                        id="kms_tolerance_pct"
                        name="kms_tolerance_pct"
                        type="number"
                        min={0}
                        max={100}
                        step={0.1}
                        value={formik.values.kms_tolerance_pct}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        invalid={pctInvalid}
                        aria-invalid={pctInvalid}
                        aria-describedby="kms_tolerance_pct_help"
                      />
                      <FormText id="kms_tolerance_pct_help">
                        {t("AutovalKmsTolerancePctHelp") ||
                          "Se ingresa en porcentaje (p. ej. 10 = 10%): diferencia máxima permitida entre kms registrados y saneados."}
                      </FormText>
                      {pctInvalid && (
                        <FormFeedback role="alert" aria-live="assertive">
                          {formik.errors.kms_tolerance_pct}
                        </FormFeedback>
                      )}
                    </FormGroup>

                    {/* Tolerancia de desviación de dirección en METROS (entero > 0). */}
                    <FormGroup>
                      <Label htmlFor="address_deviation_tolerance_m">
                        {t("AddressDeviationTolerance") || "Tolerancia de desviación de dirección (m)"}
                      </Label>
                      <Input
                        id="address_deviation_tolerance_m"
                        name="address_deviation_tolerance_m"
                        type="number"
                        min={1}
                        step={1}
                        value={formik.values.address_deviation_tolerance_m}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        invalid={devInvalid}
                        aria-invalid={devInvalid}
                        aria-describedby="address_deviation_tolerance_m_help"
                      />
                      <FormText id="address_deviation_tolerance_m_help">
                        {t("AddressDeviationToleranceHelp") ||
                          "Unidad: metros. Distancia máxima permitida entre la dirección de la escala con la que se creó el servicio y la registrada por el operador."}
                      </FormText>
                      {devInvalid && (
                        <FormFeedback role="alert" aria-live="assertive">
                          {formik.errors.address_deviation_tolerance_m}
                        </FormFeedback>
                      )}
                    </FormGroup>

                    {/* Empresas excluidas de la regla de monto>0 en aprobación
                        de PODs. Mismo selector de empresas de lista/kanban, en
                        modo multi; persiste la lista de partner_id (strings). */}
                    <FormGroup>
                      <Label htmlFor="pods_approval_excluded_company_ids">
                        {t("PodsApprovalExcludedCompanies") ||
                          "Empresas excluidas de la aprobación de PODs"}
                      </Label>
                      <Select
                        inputId="pods_approval_excluded_company_ids"
                        classNamePrefix="select2-selection"
                        isMulti
                        isClearable
                        placeholder={t("Company")}
                        options={companies}
                        value={companies.filter((c) =>
                          (formik.values.pods_approval_excluded_company_ids || [])
                            .map((v) => String(v))
                            .includes(String(c.value)),
                        )}
                        onChange={(opts) =>
                          formik.setFieldValue(
                            "pods_approval_excluded_company_ids",
                            (opts || []).map((o) => String(o.value)),
                          )
                        }
                        aria-describedby="pods_approval_excluded_help"
                      />
                      <FormText id="pods_approval_excluded_help">
                        {t("PodsApprovalExcludedCompaniesHelp") ||
                          "A estas empresas NO se les exige monto mayor a 0 para aprobar PODs de caseta, peaje o estacionamiento."}
                      </FormText>
                    </FormGroup>

                    <div className="d-flex justify-content-end">
                      <Button
                        type="submit"
                        color="primary"
                        disabled={saving || !canWrite}
                        title={!canWrite ? (t("NoPermissions") || "Sin permisos para guardar") : undefined}
                      >
                        {saving ? <Spinner size="sm" className="me-1" /> : <i className="bx bx-save me-1" />}
                        {t("Save") || "Guardar"}
                      </Button>
                    </div>
                  </Form>
                </CardBody>
              </Card>
            </Col>

            {/* Bitácora de cambios de la configuración (quién/cuándo/diff). */}
            <Col lg={6}>
              <Card>
                <CardBody>
                  <CardTitle tag="h5" className="mb-3">
                    {t("ConfigChangeHistory") || "Historial de cambios"}
                  </CardTitle>

                  {auditError && (
                    <Alert color="warning" className="py-2">
                      {t("ConfigChangeHistoryError") ||
                        "No se pudo cargar el historial de cambios."}
                    </Alert>
                  )}

                  {auditEntries.length === 0 ? (
                    <p className="text-muted mb-0">
                      {t("ConfigChangeHistoryEmpty") || "Sin cambios registrados."}
                    </p>
                  ) : (
                    <div className="table-responsive">
                      <Table className="table-sm mb-0">
                        <thead>
                          <tr>
                            <th>{t("ConfigChangeAt") || "Fecha"}</th>
                            <th>{t("ConfigChangeBy") || "Usuario"}</th>
                            <th>{t("ConfigChangeField") || "Campo"}</th>
                            <th>{t("ConfigChangeBefore") || "Antes"}</th>
                            <th>{t("ConfigChangeAfter") || "Después"}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {auditEntries.flatMap((entry, i) => {
                            const changes = entry?.changes && typeof entry.changes === "object"
                              ? entry.changes
                              : {}
                            const fields = Object.keys(changes)
                            const who = entry?.changed_by_name || entry?.changed_by || "—"
                            const when = entry?.created_at || "—"
                            if (fields.length === 0) {
                              return [(
                                <tr key={`e-${i}`}>
                                  <td>{when}</td>
                                  <td>{who}</td>
                                  <td colSpan={3} className="text-muted">—</td>
                                </tr>
                              )]
                            }
                            return fields.map((field, j) => (
                              <tr key={`e-${i}-${field}`}>
                                {j === 0 && (
                                  <td rowSpan={fields.length}>{when}</td>
                                )}
                                {j === 0 && (
                                  <td rowSpan={fields.length}>{who}</td>
                                )}
                                <td>{field}</td>
                                <td>{JSON.stringify(changes[field]?.before)}</td>
                                <td>{JSON.stringify(changes[field]?.after)}</td>
                              </tr>
                            ))
                          })}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}

        <ToastContainer
          position="bottom-right"
          autoClose={4000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          pauseOnHover
        />
      </Container>
    </div>
  )
}

export default withTranslation()(ServiceValidationAutovalConfig)
